import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { addToBag, lineKey, useBag } from './bag';
import { getOptions, lineFor } from './productOptions';
import { defaultSize, isComplete, preselect, rememberSize } from './OptionPicker';
import { openBag } from './ui';

/** Option picking + add-to-bag / buy-now for one product, shared by the product page and
 *  quick view so both behave identically (same auto-picks, stock caps and prompts). */
export function usePurchase(product) {
  const navigate = useNavigate();
  const bag = useBag();
  const options = useMemo(() => getOptions(product), [product]);
  const [color, setColor] = useState(null);
  const [size, setSize] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [missing, setMissing] = useState(0); // add presses with a choice still to make — see OptionPicker

  useEffect(() => {
    const picked = preselect(options, product?.imageUrls?.[0] || product?.thumbnailUrl);
    setColor(picked.color); setSize(picked.size); setQuantity(1); setMissing(0);
    // options is derived from product, so it already changes whenever product does.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [options]);

  const complete = isComplete(options, color, size);
  const line = product ? lineFor(product, options, color, size) : null;
  const soldOut = product?.itemsAvailable === 0;
  const stock = complete ? line?.stock : product?.itemsAvailable;
  const inBag = complete && line ? (bag.find(l => l.key === lineKey(line))?.quantity || 0) : 0;
  const maxAdd = stock != null ? Math.max(0, stock - inBag) : 99;
  const qty = Math.max(1, Math.min(Number(quantity) || 1, maxAdd || 1));

  const pickColor = (c) => {
    setColor(c); setMissing(0);
    // Keep the size when the new colour has it in stock, else move to that colour's default.
    if (options.kind === 'combo' && !options.sizesFor(c).some(s => s.value === size && s.available)) {
      setSize(defaultSize(options, c));
    }
  };
  const pickSize = (s) => {
    setSize(s); setMissing(0);
    if (options.kind !== 'label') rememberSize(s);
  };

  const add = () => {
    if (!complete) { setMissing(m => m + 1); return false; }
    if (soldOut || maxAdd <= 0) return false;
    addToBag({ ...line, quantity: qty }, stock ?? 99);
    setQuantity(1);
    return true;
  };
  const addAndOpenBag = () => { if (add()) openBag(); };
  // Checkout isn't built yet (phase 2): Buy now goes straight to the bag page for now.
  const buyNow = () => { if (add()) navigate('/bag'); };

  return {
    options, color, size, pickColor, pickSize, quantity, setQuantity, qty, missing,
    complete, line, soldOut, stock, inBag, maxAdd, add, addAndOpenBag, buyNow,
    variantImage: options.kind === 'combo' && color
      ? product?.variants?.find(v => v.color === color && v.imageUrl)?.imageUrl || null
      : null,
  };
}
